/**
 * Shared fleet telemetry.
 *
 * Live Operations' right-hand rail and the server-fleet page read the same live
 * document — the pool overview — and draw the same gauges from it. This module is the single copy of that wiring: the query, the
 * host/overview wire types, the honest null-aware helpers (an unmeasured axis is
 * hatched, never a zero bar), the reused metric rows, the sample-health tone and
 * the resizable-column hook. Both screens import from here, so "what counts as
 * stale" and "how an unknown gauge draws" can only be changed in one place.
 *
 * The document arrives over /ws/events and is written into this query's cache by
 * useSystemStatus: the server pushes a `state` frame on the telemetry cadence,
 * so the gauges move when the samples do rather than when a timer fires. The
 * fetch here is the first paint and the fallback for a console whose socket is
 * down — it used to be the only source, which is why nothing on these screens
 * moved without a poll or a page reload.
 */

import {
  useCallback,
  useEffect,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useSystemStatus } from '@/hooks/useSystemStatus'
import { Meter, StatusDot, type Tone } from '@/components/primitives'
import type { AccountUsage } from '@/components/AccountUsage'
import type { GpuMetric, HostMetrics, HostStatus, ID, ISOTime, NodeStatus } from '@/types/domain'
import { useT } from '@/i18n/context'

/* ------------------------------------------------------------ Wire shapes */

/** domain.CLIInfo — a discovered provider CLI. */
export interface CLIInfo {
  installed: boolean
  path?: string
  version?: string
}

/** domain.HostCapabilities — the remote inventory, every field of which is
 *  omitempty except the two tool booleans. */
export interface HostCapabilities {
  os?: string
  arch?: string
  kernelInfo?: string
  cpuCores?: number
  memoryBytes?: number
  diskBytes?: number
  gpus?: string[]
  hasTmux: boolean
  hasGit: boolean
  claudeCli?: CLIInfo
  codexCli?: CLIInfo
  discoveredAt?: ISOTime
}

/**
 * api.HostResponse — a managed machine as the wire redacts it.
 *
 * There is no private-key path and no fingerprint verdict here: only a
 * connection attempt can say whether the machine still presents its pinned key,
 * and that verdict lives on the test response, not on the host record.
 */
export interface HostResponse {
  id: ID
  name: string
  address: string
  port: number
  user: string
  sshConfigAlias?: string
  authMethod: string
  privateKeyConfigured: boolean
  hostKeyFingerprint?: string
  proxyJump?: string
  labels?: string[]
  status: HostStatus
  nodeStatus: NodeStatus
  isControlHost: boolean
  nodeVersion?: string
  /** The digest of the node's own dependency closure, as the agent reports it.
   *  Absent when the agent predates the stamp. */
  nodeContract?: string
  /** Whether restarting the agent would change how it behaves. Derived per
   *  response against this build's own digest, never stored — a stored verdict
   *  would be stale within one deploy. Absent from an older control plane, and
   *  absence is unmeasured rather than "no". */
  nodeContractState?: 'MATCHES' | 'DIFFERS' | 'UNMEASURED'
  lastSeenAt?: ISOTime
  bootId?: string
  capabilities: HostCapabilities
  /** api.CLIStatusResponse[]: each provider CLI on this host against the newest
   *  version its vendor publishes. Absent on a deployment with no release check
   *  running — which is why `freshness` has an UNKNOWN of its own rather than
   *  defaulting to "current". */
  clis?: CLIStatus[]
  createdAt: ISOTime
  updatedAt: ISOTime
  version: number
}

/** How an installed CLI stands against the newest published one. UNKNOWN means
 *  one of the two versions is missing, never "probably fine". */
export type CLIFreshness = 'UNKNOWN' | 'CURRENT' | 'BEHIND' | 'AHEAD'

export interface CLIStatus {
  provider: string
  installed: boolean
  version?: string
  latest?: string
  freshness: CLIFreshness
  checkedAt?: ISOTime
}

/**
 * One host in the overview: api.HostOverview.
 *
 * The server embeds the full HostResponse (identity, capabilities and status,
 * promoted onto the wire) and adds the live `metrics` block. The fleet reads
 * identity, inventory and utilisation off this one document — there is no second
 * request per host.
 */
export interface OverviewHost extends HostResponse {
  metrics: HostMetrics
}

/** One open risk. `params` are the values the server interpolated into its own
 *  English `title`; a consumer composes a localised sentence from them and falls
 *  back to `title` for a kind it does not recognise. */
export interface OverviewRisk {
  kind: string
  severity: string
  title: string
  /** The server's own English sentence, for a kind this build does not
   *  recognise and for a tooltip that wants more than the headline. */
  detail?: string
  /** What the risk was raised about — `"host"`, `"account"` — and which one.
   *  The server has always sent these (api.Risk); declaring them is what lets a
   *  panel show only the risks standing under the session it is describing,
   *  rather than every risk in the fleet next to one terminal. */
  targetType?: string
  targetId?: ID
  params?: Record<string, string>
}

/** Where acting on a recommendation starts: conductor.Action. `kind` names the
 *  act so the console can offer its own control for it; `path` is the route for
 *  a console that knows no better. Neither performs anything. */
export interface RecommendationAction {
  kind: string
  path: string
  /** The suggested target of a switch — a prefill for the dialog, never a
   *  choice made on the operator's behalf. */
  accountProfileId?: ID
  /** The task a start_session or open_task action is about. */
  taskId?: ID
}

/** One thing the conductor says could be done next: conductor.Recommendation.
 *  Shaped like a risk (kind + params for the console's own sentence, English
 *  title as the fallback) plus the action a risk has not got. */
export interface OverviewRecommendation {
  kind: string
  severity: string
  title: string
  detail?: string
  params?: Record<string, string>
  targetType?: string
  targetId?: ID
  since?: ISOTime
  action: RecommendationAction
}

/** The slice of api.OverviewResponse both screens read. Every field is a real
 *  count or measurement. */
export interface OverviewData {
  /** Optional because the wire is not a promise. A server that predates the
   *  counter block, or a degraded response, must leave a reader with nothing to
   *  show rather than a property access on undefined — this document is read by
   *  the top bar, which is on every screen. The day's counters are optional
   *  within it too: only the overview runs the query behind them, and the
   *  connect-time snapshot carries the block without them. */
  sessions?: { active: number; waitingInput: number; completed24h?: number; failed24h?: number }
  hosts: OverviewHost[]
  accounts: Array<{ id: ID; label: string; usage?: AccountUsage; canAcceptWork?: boolean }>
  risks: OverviewRisk[]
  /** The conductor's advice. Optional until every server sends it: an older
   *  build, or a capture taken before it existed, must render as "nothing to
   *  say yet", not as a crash. */
  recommendations?: OverviewRecommendation[]
}

/**
 * One overview document, shared by every consumer and pushed rather than polled.
 *
 * They all read the same document, so they key the same query and TanStack
 * dedupes them. The socket writes this cache directly (`useSystemStatus`), so
 * the fetch below is the *first* paint and the fallback — not the steady state.
 *
 * Polling resumes only when the socket is not open. That inversion is the whole
 * change: a connected console now sees a host go offline when it goes offline,
 * and a disconnected one still tells the truth, a little later.
 */
export function useOverview() {
  const { connection } = useSystemStatus()
  return useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get<OverviewData>('/system/overview'),
    // 15s matches the node's own publish cadence: faster would re-fetch numbers
    // that have not moved at the source.
    refetchInterval: connection === 'open' ? false : 15_000,
  })
}

/* -------------------------------------------------------------- Helpers */

export const GiB = 1024 ** 3

/** A percent (0..100) as a meter fraction (0..1), or null when unmeasured — so
 *  the Meter draws its hatched "unknown" track rather than an empty (zero) bar. */
export function frac(pct: number | null | undefined): number | null {
  return pct == null ? null : Math.min(1, pct / 100)
}

/** Utilisation tone: calm phosphor while there is headroom, amber as it tightens,
 *  red when it is nearly gone. Neutral for an unmeasured value, which has no
 *  severity to claim. */
export function utilTone(pct: number | null | undefined): Tone {
  if (pct == null) return 'neutral'
  if (pct >= 90) return 'err'
  if (pct >= 70) return 'warn'
  return 'accent'
}

/** Bytes as a GiB figure. Whole numbers past 100 (a disk), one decimal below
 *  (memory), so "21.4" and "715" both read cleanly. */
export function fmtGiB(bytes: number | undefined): string {
  if (!bytes) return '—'
  const g = bytes / GiB
  return g >= 100 ? String(Math.round(g)) : g.toFixed(1)
}

/**
 * Sample health, shared so the two screens agree on what "stale" and "live"
 * mean. `metricsStaleForSeconds` is the sample clock: it is set only when a live
 * sample exists, so its presence is the honest test for "has this host reported
 * utilisation at all", distinct from `staleForSeconds` (presence since last
 * contact).
 */
/* Both take an absent block, and answer "no sample" for it.
 
   The wire always carries `metrics` — it is a bare struct with no omitempty, so
   Go serialises `{}` for a host that has never reported — and the type says so.
   But these two are reached from four screens and a missing block threw a
   TypeError out of a render, which React turns into a blank page: the whole
   console, gone, because one host had not been sampled. A total function costs
   two characters and removes that as a possibility. */
export function hostSampled(m?: HostMetrics): boolean {
  return m?.metricsStaleForSeconds != null
}

export function hostStale(m?: HostMetrics): boolean {
  return m?.metricsStaleForSeconds != null && m.metricsStaleForSeconds > 60
}

/**
 * The sample-health tone for a host's live dot. An unreachable host is an error;
 * a sample older than a minute is stale (warn, keep the last values); a fresh
 * sample is ok; no sample at all is unknown — never a confident green. This is a
 * different question from the host's *status* tone (hostStatusTone), which is
 * why it does not fold DEGRADED into the error case: a degraded host that is
 * still reporting has a healthy sample.
 */
export function dotTone(host: OverviewHost): Tone {
  if (host.status === 'OFFLINE' || host.status === 'UNREACHABLE') return 'err'
  if (hostStale(host.metrics)) return 'warn'
  if (hostSampled(host.metrics)) return 'ok'
  return 'unknown'
}

/** The pulse is reserved for a host that is genuinely reporting right now:
 *  online, sampled, and the sample is under a minute old. */
export function hostLive(host: OverviewHost): boolean {
  return host.status === 'ONLINE' && hostSampled(host.metrics) && !hostStale(host.metrics)
}

/* --------------------------------------------------------- Metric rows */

/** One gauge row: fixed label · fill bar · percent · optional trailing detail
 *  (used/total memory, GPU temperature). Percent and detail are mono because
 *  they are machine facts; the label is the sans word for the axis. */
export function MetricRow({ label, pct, detail }: { label: string; pct?: number; detail?: string }) {
  return (
    <div className="k-hm__row">
      <span className="k-hm__label">{label}</span>
      <Meter value={frac(pct)} tone={utilTone(pct)} unknownLabel="—" ariaLabel={label} />
      <span className="k-hm__pct k-mono">{pct == null ? '—' : `${Math.round(pct)}%`}</span>
      <span className="k-hm__detail k-mono">{detail ?? ''}</span>
    </div>
  )
}

/** A gauge per accelerator. A single maximum hides the card that is pinned while
 *  the rest idle, so each GPU gets its own row; when the fleet is one model, its
 *  name is stated once above the rows rather than repeated on every line. */
export function GpuRows({ gpus, t }: { gpus: GpuMetric[]; t: ReturnType<typeof useT> }) {
  const multi = gpus.length > 1
  const firstName = gpus[0]?.name
  const model = firstName && gpus.every((g) => g.name === firstName) ? firstName : null
  return (
    <>
      {multi && model && (
        <div className="k-hm__gpucap k-mono">
          {gpus.length} × {model}
        </div>
      )}
      {gpus.map((g) => (
        <MetricRow
          key={g.index}
          label={multi ? `${t('live.metric.gpu')} ${g.index}` : t('live.metric.gpu')}
          pct={g.utilPercent}
          detail={gpuDeviceDetail(g)}
        />
      ))}
    </>
  )
}

/** Temperature (the health signal) and used/total VRAM for one card. */
export function gpuDeviceDetail(g: GpuMetric): string | undefined {
  const mem =
    g.memUsedMb && g.memTotalMb
      ? `${(g.memUsedMb / 1024).toFixed(1)}/${Math.round(g.memTotalMb / 1024)}G`
      : g.memUsedMb
        ? `${(g.memUsedMb / 1024).toFixed(1)}G`
        : null
  const parts = [g.tempC != null ? `${Math.round(g.tempC)}°C` : null, mem].filter(Boolean)
  return parts.length ? parts.join(' · ') : undefined
}

/* ------------------------------------------------------------- Freshness */

/**
 * How long ago this document arrived, and whether the socket that carries it is
 * open.
 *
 * Both halves matter, and they answer different questions. A live dot with a
 * climbing age says "connected, but nothing is arriving" — a different fault
 * from "the socket dropped", and the two were indistinguishable before this
 * existed. The clock ticks on its own second so the age counts up between frames
 * rather than freezing at whatever it was when the last one landed.
 *
 * It lives here rather than on the fleet screen because the dashboard shows the
 * same document and needs the same claim about it. A second copy would be a
 * second definition of "how fresh is fresh", on the two screens most likely to
 * disagree about it.
 */
export function Freshness({ updatedAt }: { updatedAt: number }) {
  const t = useT()
  const { connection } = useSystemStatus()
  const [, tick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [])
  const secs = updatedAt ? Math.max(0, Math.round((Date.now() - updatedAt) / 1000)) : null
  const connTone: Tone = connection === 'open' ? 'ok' : connection === 'closed' ? 'err' : 'warn'
  return (
    <div className="k-fleet__fresh">
      <span className="k-fleet__fresh-upd">
        <span className="k-fleet__fresh-word">{t('fleet.updated')}</span>{' '}
        <span className="k-mono">{secs == null ? '—' : `${secs}sn`}</span>
      </span>
      <span className="k-fleet__fresh-conn">
        <StatusDot tone={connTone} live={connection === 'open'} label={connection} />
        <span className="k-fleet__fresh-lbl">{t('fleet.stream')}</span>
      </span>
    </div>
  )
}

/* ------------------------------------------------- Resizable columns hook */

export interface ColumnWidths {
  left: number
  right: number
}

const COL_LIMITS = { leftMin: 208, leftMax: 460, rightMin: 264, rightMax: 560 }

function clampWidth(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v))
}

function readWidths(storageKey: string, defaults: ColumnWidths): ColumnWidths {
  try {
    const raw = localStorage.getItem(storageKey)
    if (raw) {
      const p = JSON.parse(raw) as Partial<ColumnWidths>
      return {
        left: clampWidth(Number(p.left) || defaults.left, COL_LIMITS.leftMin, COL_LIMITS.leftMax),
        right: clampWidth(Number(p.right) || defaults.right, COL_LIMITS.rightMin, COL_LIMITS.rightMax),
      }
    }
  } catch {
    // A corrupt preference is not worth failing the page over; fall through to
    // the defaults, which are the same widths the layout ships with.
  }
  return { ...defaults }
}

/**
 * The side columns are user-resizable; the flexing column takes the rest.
 *
 * Widths persist to localStorage so an operator's chosen balance survives a
 * reload. Two screens use this with different keys and defaults (their layouts
 * do not share a sensible width), passed in `opts`; Live Operations calls it
 * with no argument and keeps its original key and defaults exactly. The drag is
 * on the window rather than the handle so the pointer can leave the 6px gutter
 * mid-drag without the resize stopping.
 */
export function useResizableColumns(opts?: { storageKey?: string; defaults?: ColumnWidths }) {
  const storageKey = opts?.storageKey ?? 'kimene.lo.cols'
  const defaults = opts?.defaults ?? { left: 288, right: 336 }
  const [widths, setWidths] = useState<ColumnWidths>(() => readWidths(storageKey, defaults))

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(widths))
  }, [storageKey, widths])

  const startDrag = useCallback(
    (edge: 'left' | 'right', e: ReactPointerEvent) => {
      e.preventDefault()
      const startX = e.clientX
      const start = { ...widths }
      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX
        setWidths((w) =>
          edge === 'left'
            ? { ...w, left: clampWidth(start.left + dx, COL_LIMITS.leftMin, COL_LIMITS.leftMax) }
            : { ...w, right: clampWidth(start.right - dx, COL_LIMITS.rightMin, COL_LIMITS.rightMax) },
        )
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        document.body.style.userSelect = ''
        document.body.style.cursor = ''
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'col-resize'
    },
    [widths],
  )

  return { widths, startDrag }
}
